import type { Edge, Node } from '@xyflow/react'
import { getNodeDefinition, type NodeDefinition } from '@/shared/lib/node-registry'
import { getOp, getOpsByNodeType, resolveOpAcceptSourceTypes, resolveOpMaxSourceConnections } from '@/shared/lib/op-registry'
import { readNodeCustomDataSnapshot } from './node-custom-data'

export type EdgeLike = Pick<Edge, 'source' | 'target'>

/**
 * 读取节点对外提供的连接类型。
 *
 * @param definition 节点注册定义
 * @returns 显式 provides 优先；没有时回退节点类型
 */
export function getNodeOutputKind(definition: NodeDefinition): string | null {
  return definition.type
}

/**
 * 判断两节点之间是否已经存在直接边。
 *
 * @param edges 当前画布边
 * @param source source 节点 ID
 * @param target target 节点 ID
 */
function hasDirectConnection({
  edges,
  source,
  target,
}: {
  edges: EdgeLike[]
  source: string
  target: string
}) {
  return edges.some((edge) => edge.source === source && edge.target === target)
}

/**
 * 判断新增 source -> target 是否会形成环。
 *
 * @param edges 当前画布边
 * @param source source 节点 ID
 * @param target target 节点 ID
 */
function wouldCreateConnectionCycle({
  edges,
  source,
  target,
}: {
  edges: EdgeLike[]
  source: string
  target: string
}) {
  const stack = [target]
  const visited = new Set<string>()

  while (stack.length > 0) {
    const nodeId = stack.pop()

    if (!nodeId || visited.has(nodeId)) {
      continue
    }

    if (nodeId === source) {
      return true
    }

    visited.add(nodeId)

    for (const edge of edges) {
      if (!edge.source || !edge.target || edge.source !== nodeId) {
        continue
      }

      stack.push(edge.target)
    }
  }

  return false
}

/**
 * 按有效 sourceKind 校验目标节点是否接收该输入。
 *
 * 普通节点 sourceKind 来自节点定义；slot 单项连接会把列表格子伪装成 image/text/video/audio。
 * OP 节点优先使用 op.acceptSourceTypes，因为同一个目标节点类型可能绑定不同 OP。
 */
export function canConnectSourceKindToTarget({
  allowEmptyOpAccepts = false,
  edges,
  nodes,
  sourceKind,
  target,
}: {
  allowEmptyOpAccepts?: boolean
  edges?: EdgeLike[]
  nodes: Node[]
  sourceKind?: string | null
  target?: string | null
}) {
  if (!sourceKind || !target) {
    return false
  }

  const targetOpValue = readNodeCustomDataSnapshot(target).op
  const targetOpDefinition = typeof targetOpValue === 'string' && targetOpValue
    ? getOp(targetOpValue)
    : undefined

  // 基于互斥连线组 (Exclusive Input Group) 进行拦截
  // 如果目标节点已经连接了属于某互斥组的源节点，则只能继续连接同种实际类型的节点
  if (!targetOpDefinition?.allowMixedExclusiveInputGroup && edges && edges.length > 0) {
    const incomingEdges = edges.filter((e) => e.target === target)
    if (incomingEdges.length > 0) {
      let existingExclusiveGroup: string | null = null
      let existingSourceType: string | null = null

      for (const edge of incomingEdges) {
        const sourceNode = nodes.find((n) => n.id === edge.source)
        const sType = typeof sourceNode?.type === 'string' ? sourceNode.type : ''
        if (sType) {
          const sDef = getNodeDefinition(sType)
          if (sDef?.exclusiveInputGroup) {
            existingExclusiveGroup = sDef.exclusiveInputGroup
            existingSourceType = sType
            break
          }
        }
      }

      // 如果目标节点已连的源节点带有互斥组
      if (existingExclusiveGroup) {
        const currentSourceDef = sourceKind ? getNodeDefinition(sourceKind) : undefined
        // 如果新连入的源节点也属于同一个互斥组，则要求它们的节点类型必须完全相同
        if (currentSourceDef?.exclusiveInputGroup === existingExclusiveGroup) {
          if (sourceKind !== existingSourceType) {
            return false
          }
        }
      }
    }
  }

  const targetNode = nodes.find((node) => node.id === target)
  const targetType = typeof targetNode?.type === 'string' ? targetNode.type : ''
  if (!targetType) {
    return false
  }

  const targetOp = readNodeCustomDataSnapshot(target).op
  if (typeof targetOp === 'string' && targetOp) {
    const op = getOp(targetOp)
    if (!op || !op.targetNodeTypes.includes(targetType)) {
      return false
    }

    const resolvedAcceptSourceTypes = resolveOpAcceptSourceTypes(op, targetType)

    // 普通连接沿用原先的严格语义；slot 引用可按菜单规则把空 acceptSourceTypes 视为不限制。
    const defaultAccepts = allowEmptyOpAccepts
      ? (!resolvedAcceptSourceTypes?.length || resolvedAcceptSourceTypes.includes(sourceKind))
      : Boolean(resolvedAcceptSourceTypes?.includes(sourceKind))

    return defaultAccepts
  }

  // 如果节点尚未选择特定的 OP，只要有任何适用于该节点类型的 OP 接受此 sourceKind 即可
  const ops = getOpsByNodeType(targetType)
  for (const op of ops) {
    const resolvedAcceptSourceTypes = resolveOpAcceptSourceTypes(op, targetType)

    const accepts = allowEmptyOpAccepts
      ? (!resolvedAcceptSourceTypes?.length || resolvedAcceptSourceTypes.includes(sourceKind))
      : Boolean(resolvedAcceptSourceTypes?.includes(sourceKind))

    if (accepts) {
      return true
    }
  }

  return false
}

export function getTargetMaxConnections(
  nodes: { id: string; type?: string }[] | Record<string, { id: string; type?: string }>,
  target: string
): number {
  const targetNode = Array.isArray(nodes)
    ? nodes.find((node) => node.id === target)
    : nodes[target]
  const targetType = typeof targetNode?.type === 'string' ? targetNode.type : ''
  if (!targetType) return Infinity

  const targetOp = readNodeCustomDataSnapshot(target).op
  if (typeof targetOp === 'string' && targetOp) {
    const op = getOp(targetOp)
    if (op) {
      return resolveOpMaxSourceConnections(op, targetType)
    }
  }
  return Infinity
}

/**
 * 使用指定 sourceKind 校验一条真实 source -> target 连接。
 *
 * @param edges 当前画布边
 * @param nodes 当前画布节点
 * @param target 真实 target 节点 ID
 */
export function hasReachedMaxConnections({
  edges,
  nodes,
  target,
}: {
  edges: EdgeLike[]
  nodes: Node[]
  target: string
}) {
  const maxConnections = getTargetMaxConnections(nodes, target)

  if (maxConnections !== Infinity) {
    const incomingEdgeCount = edges.filter((e) => e.target === target).length
    return incomingEdgeCount >= maxConnections
  }

  return false
}

/**
 * 当为目标节点新增一条连线时，计算需要被挤占（移除）的最旧连线。
 *
 * @param edges 当前画布边
 * @param nodes 当前画布节点
 * @param target 目标节点 ID
 * @returns 需要被移除的边数组
 */
export function getEdgesToReplaceForTarget<T extends EdgeLike & { id: string }>({
  edges,
  nodes,
  target,
  source,
  incomingSourceNodeType,
}: {
  edges: T[]
  nodes: { id: string; type?: string }[] | Record<string, { id: string; type?: string }>
  target: string
  source?: string
  incomingSourceNodeType?: string
}): T[] {
  const targetNode = Array.isArray(nodes) ? nodes.find((n) => n.id === target) : nodes[target]
  const targetOpStr = readNodeCustomDataSnapshot(target).op
  const op = typeof targetOpStr === 'string' ? getOp(targetOpStr) : null

  if (op?.getEdgesToReplace) {
    const sourceNode = source ? (Array.isArray(nodes) ? nodes.find((n) => n.id === source) : nodes[source]) : null
    const resolvedSourceNodeType = incomingSourceNodeType || sourceNode?.type || ''
    
    return op.getEdgesToReplace({
      targetNodeType: targetNode?.type || '',
      targetNodeId: target,
      incomingSourceNodeId: source,
      incomingSourceNodeType: resolvedSourceNodeType,
      currentIncomingEdges: edges.filter((e) => e.target === target),
      nodes,
    }) as T[]
  }

  const maxConnections = getTargetMaxConnections(nodes, target)

  if (maxConnections !== Infinity) {
    // 找出所有连向该目标节点的边
    const incomingEdges = edges.filter((e) => e.target === target)
    // 加入 1 条新边后，如果超出了 maxConnections，则移除旧的
    const excess = incomingEdges.length + 1 - maxConnections
    if (excess > 0) {
      // 假设 edges 数组越靠前的是越早连的（React Flow 默认如此），返回前面的 excess 条
      return incomingEdges.slice(0, excess)
    }
  }

  return []
}

export function canConnectNodesWithSourceKind({
  allowEmptyOpAccepts,
  edges,
  nodes,
  source,
  sourceKind,
  target,
}: {
  allowEmptyOpAccepts?: boolean
  edges: EdgeLike[]
  nodes: Node[]
  source?: string | null
  sourceKind?: string | null
  target?: string | null
}) {
  if (!source || !target || source === target) {
    return false
  }

  if (hasDirectConnection({ edges, source, target })) {
    return false
  }

  if (wouldCreateConnectionCycle({ edges, source, target })) {
    return false
  }

  return canConnectSourceKindToTarget({
    allowEmptyOpAccepts,
    edges,
    nodes,
    sourceKind,
    target,
  })
}

export function canConnectNodeTypes({
  sourceType,
  targetType,
}: {
  sourceType?: string | null
  targetType?: string | null
}) {
  if (!sourceType || !targetType) {
    return false
  }

  const sourceDefinition = getNodeDefinition(sourceType)
  const targetDefinition = getNodeDefinition(targetType)

  if (!sourceDefinition || !targetDefinition) {
    return false
  }

  const outputKind = getNodeOutputKind(sourceDefinition)
  if (!outputKind) {
    return false
  }

  const ops = getOpsByNodeType(targetType)
  for (const op of ops) {
    const resolvedAcceptSourceTypes = resolveOpAcceptSourceTypes(op, targetType)

    if (resolvedAcceptSourceTypes?.includes(outputKind)) {
      return true
    }
  }

  return false
}

export function canConnectNodes({
  edges,
  nodes,
  source,
  target,
}: {
  edges: EdgeLike[]
  nodes: Node[]
  source?: string | null
  target?: string | null
}) {
  if (!source || !target || source === target) {
    return false
  }

  const sourceNode = nodes.find((node) => node.id === source)

  const sourceType = typeof sourceNode?.type === 'string' ? sourceNode.type : ''
  const sourceDefinition = sourceType ? getNodeDefinition(sourceType) : undefined
  const sourceKind = sourceDefinition ? getNodeOutputKind(sourceDefinition) : null

  return canConnectNodesWithSourceKind({
    edges,
    nodes,
    source,
    sourceKind,
    target,
  })
}
